import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from '@inboundx/shared/config';
import {
    ArrowLeft,
    ArrowRight,
    Building2,
    Check,
    Globe,
    Info,
    Languages,
    LogOut,
    Plus,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackOnboardingStep } from '@/analytics/onboardingFunnel';
import { OnboardingShell, WhyCard } from '@/components/onboarding/OnboardingShell';
import { setWelcome, type WelcomeLoaderState } from '@/components/onboarding/welcomeFlow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { getSchemaBySlug } from '@/config/schemas';
import { cn } from '@/lib/utils';
import { fetchSiteMetadata } from '@/services/siteBrand';

/**
 * Self-serve onboarding wizard (IX-3768).
 *
 * Shown by AuthGuard when a user is authenticated but has no backoffice_users
 * row yet. Collects the minimal setup answers client-side and submits them as
 * a single atomic claim — no rows are created until the final step.
 */

interface OnboardingWizardProps {
    email: string;
    onSignOut: () => void;
    /**
     * Staff (`backoffice_users.is_admin`) — shows the force re-onboard toggle
     * even when the mailbox is not `@userose.ai`. Unknown while the user has no
     * backoffice row yet, which is what the email fallback covers.
     */
    isStaff?: boolean;
}

type StepId = 'website' | 'company' | 'language';

const STEPS: StepId[] = ['website', 'company', 'language'];

/** Tab title per step — a wizard step is not a route, so nothing derives these. */
const STEP_TITLES: Record<StepId, string> = {
    website: 'Your website',
    company: 'Your company',
    language: 'Your language',
};

// Every language Rose supports, native-labelled. The main language picks from
// the full set in a dropdown; the others show as chips, first five then a
// "More" toggle so the row stays scannable (IX-4867).
const LANGUAGES: { value: string; label: string }[] = SUPPORTED_LANGUAGES.map((value) => ({
    value,
    label: LANGUAGE_NAMES[value],
}));
const OTHER_LANGUAGE_PREVIEW_COUNT = 5;

// Tone presets are the single source of truth in the behavior schema
// (`ui:presets`), shared with the Brand & tone page and Playground. Onboarding
// no longer asks clients to choose: it sends the Sales-oriented preset text
// verbatim so the default still highlights on Brand & tone with zero duplicated
// tone copy.
const TONE_PRESETS = (getSchemaBySlug('behavior')?.properties?.response_tone?.['ui:presets'] ??
    {}) as Record<string, string>;

// Default new clients to the sales-oriented voice. The admin API pairs it with
// the conversion-first playbook when provisioning the behavior config.
const DEFAULT_TONE_PRESET = TONE_PRESETS['Sales-oriented'] ?? '';

/** Per-step eyebrow copy shown above the title. */
const STEP_KICKERS: Record<StepId, string> = {
    website: 'Step 1 of 3',
    company: 'Step 2 of 3',
    language: 'Step 3 of 3',
};

const STEP_ASIDES = {
    website: {
        icon: Globe,
        title: 'We read your whole site',
        description:
            "Rose crawls your pages, keeps the useful ones, and loads them into your agent's memory. No manual uploads to start.",
        stat: 'Avg. 128 pages read in ~2 min',
    },
    company: {
        icon: Building2,
        title: 'Your brand, front and center',
        description: 'We prefill what we can from your domain so there is less for you to type.',
        stat: 'Prefilled from your site',
    },
    language: {
        icon: Languages,
        title: "Speak every visitor's language",
        description:
            'Your main language sets the default greeting and answers. Add every other language your site serves so Rose is ready for them too.',
        stat: 'Ten languages supported',
    },
} satisfies Record<StepId, Parameters<typeof WhyCard>[0]>;

/** Extract a site domain from free-form URL input, mirroring the backend rule. */
export function extractDomain(raw: string): string | null {
    let candidate = raw.trim().toLowerCase();
    if (!candidate) return null;
    if (!candidate.includes('//')) candidate = `https://${candidate}`;
    let host: string;
    try {
        host = new URL(candidate).hostname.replace(/\.$/, '');
    } catch {
        return null;
    }
    if (host.startsWith('www.')) host = host.slice(4);
    return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(host)
        ? host
        : null;
}

/**
 * Staff-only test affordance (IX-3930): fallback signal for the "force
 * re-onboard" toggle when the user has no `backoffice_users` row yet and the
 * `isStaff` flag is therefore unknown. The server is the real gate — it ignores
 * the flag for non-staff — so this only controls UI visibility.
 */
const STAFF_EMAIL_DOMAIN = 'userose.ai';

export function isStaffEmail(email: string): boolean {
    return email.trim().toLowerCase().endsWith(`@${STAFF_EMAIL_DOMAIN}`);
}

/** Prefill a company name from the claimed domain ("acme-corp.com" → "Acme Corp"). */
export function companyNameFromDomain(domain: string): string {
    const base = domain.split('.')[0] || '';
    return base
        .split(/[-_]/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function homepageUrlForBrandLookup(raw: string, domain: string): string {
    const trimmed = raw.trim();
    const withScheme = trimmed.includes('//') ? trimmed : `https://${trimmed || domain}`;
    try {
        const url = new URL(withScheme);
        url.hash = '';
        url.search = '';
        url.pathname = '/';
        return url.toString();
    } catch {
        return `https://${domain}/`;
    }
}

export interface WizardDraft {
    stepIndex: number;
    websiteUrl: string;
    companyName: string;
    language: string;
    additionalLanguages: string[];
    languagesEdited?: boolean;
}

/** Draft persistence: survive refresh / failed attempts without re-asking.
 *  Keyed by account email so two accounts on one browser don't mix drafts. */
function draftKey(email: string): string {
    return `rose_onboarding_draft_${email}`;
}

export function loadWizardDraft(email: string): WizardDraft | null {
    try {
        const raw = localStorage.getItem(draftKey(email));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<WizardDraft>;
        if (typeof parsed !== 'object' || parsed === null) return null;
        const language = LANGUAGES.find((lang) => lang.value === parsed.language)?.value ?? 'en';
        return {
            stepIndex: Math.min(
                Math.max(typeof parsed.stepIndex === 'number' ? parsed.stepIndex : 0, 0),
                STEPS.length - 1
            ),
            websiteUrl: typeof parsed.websiteUrl === 'string' ? parsed.websiteUrl : '',
            companyName: typeof parsed.companyName === 'string' ? parsed.companyName : '',
            language,
            languagesEdited: parsed.languagesEdited === true,
            additionalLanguages: Array.isArray(parsed.additionalLanguages)
                ? [
                      ...new Set(
                          parsed.additionalLanguages.filter(
                              (code) =>
                                  code !== language && LANGUAGES.some((lang) => lang.value === code)
                          )
                      ),
                  ]
                : [],
        };
    } catch {
        return null;
    }
}

export function saveWizardDraft(email: string, draft: WizardDraft): void {
    try {
        localStorage.setItem(draftKey(email), JSON.stringify(draft));
    } catch {
        // Storage unavailable (private mode, quota) — draft just won't survive refresh.
    }
}

export function clearWizardDraft(email: string): void {
    try {
        localStorage.removeItem(draftKey(email));
    } catch {
        // Ignore — worst case a stale draft lingers for this account.
    }
}

export function OnboardingWizard({ email, onSignOut, isStaff = false }: OnboardingWizardProps) {
    const navigate = useNavigate();
    const canForce = isStaff || isStaffEmail(email);

    const [forceOnboarding, setForceOnboarding] = useState(false);
    const [draft] = useState(() => loadWizardDraft(email));
    const [stepIndex, setStepIndex] = useState(draft?.stepIndex ?? 0);
    const [websiteUrl, setWebsiteUrl] = useState(draft?.websiteUrl ?? '');
    const [companyName, setCompanyName] = useState(draft?.companyName ?? '');
    const [language, setLanguage] = useState(draft?.language ?? 'en');
    const [additionalLanguages, setAdditionalLanguages] = useState<string[]>(
        draft?.additionalLanguages ?? []
    );
    const languagesEdited = useRef(draft?.languagesEdited ?? false);
    const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
    const [sitePrimaryColor, setSitePrimaryColor] = useState<string | undefined>();
    const [showAllOtherLanguages, setShowAllOtherLanguages] = useState(false);

    // Persist every answer so a refresh or failed attempt resumes in place.
    useEffect(() => {
        saveWizardDraft(email, {
            stepIndex,
            websiteUrl,
            companyName,
            language,
            additionalLanguages,
            languagesEdited: languagesEdited.current,
        });
    }, [email, stepIndex, websiteUrl, companyName, language, additionalLanguages]);

    const step = STEPS[stepIndex];
    const domain = useMemo(() => extractDomain(websiteUrl), [websiteUrl]);

    useEffect(() => {
        setSitePrimaryColor(undefined);
        setDetectedLanguage(null);
        if (!domain) return;

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => {
            const lookupUrl = homepageUrlForBrandLookup(websiteUrl, domain);
            void fetchSiteMetadata(lookupUrl, controller.signal).then((metadata) => {
                if (!controller.signal.aborted) {
                    setSitePrimaryColor(metadata?.primaryColor ?? undefined);
                    setDetectedLanguage(metadata?.language ?? null);
                    if (metadata && !languagesEdited.current) {
                        const mainLanguage = metadata.language ?? 'en';
                        setLanguage(mainLanguage);
                        setAdditionalLanguages(
                            metadata.additionalLanguages.filter((code) => code !== mainLanguage)
                        );
                    }
                }
            });
        }, 350);

        return () => {
            window.clearTimeout(timeoutId);
            controller.abort();
        };
    }, [domain, websiteUrl]);

    const canContinue = (() => {
        if (step === 'website') return domain !== null;
        if (step === 'company') return companyName.trim().length > 0;
        return true; // language is always valid
    })();

    const goNext = () => {
        if (step === 'website' && domain && !companyName) {
            setCompanyName(companyNameFromDomain(domain));
        }
        if (stepIndex < STEPS.length - 1) {
            trackOnboardingStep(`wizard_${step}`, domain ?? undefined);
            setStepIndex(stepIndex + 1);
        }
    };

    const goBack = () => {
        if (stepIndex > 0) setStepIndex(stepIndex - 1);
    };

    const handleSubmit = () => {
        // Last wizard step (language) completed; the claim runs on the welcome loader.
        trackOnboardingStep('wizard_language', domain ?? undefined);
        const loaderState: WelcomeLoaderState = {
            company: companyName.trim(),
            tint: sitePrimaryColor,
            pendingClaim: {
                email,
                request: {
                    website_url: websiteUrl.trim(),
                    company_name: companyName.trim(),
                    language,
                    additional_languages: additionalLanguages.filter((code) => code !== language),
                    tone_preset: DEFAULT_TONE_PRESET,
                    brand_color: sitePrimaryColor,
                    force: forceOnboarding || undefined,
                },
            },
        };
        // Let the route guard render the loader before provisioning has created
        // a backoffice user. The token itself stays in navigation state only.
        setWelcome({
            company: loaderState.company,
            domain: domain ?? '',
            tint: sitePrimaryColor,
            claimPending: true,
        });
        navigate('/welcome/loader', { replace: true, state: loaderState });
    };

    const handleSignOut = () => {
        clearWizardDraft(email);
        onSignOut();
    };

    const isLastStep = stepIndex === STEPS.length - 1;

    // Other-language chips are every supported language except the main one.
    const otherLanguages = LANGUAGES.filter((lang) => lang.value !== language);
    const visibleOtherLanguages = showAllOtherLanguages
        ? otherLanguages
        : otherLanguages.filter(
              (lang, index) =>
                  index < OTHER_LANGUAGE_PREVIEW_COUNT || additionalLanguages.includes(lang.value)
          );
    const toggleAdditionalLanguage = (code: string) => {
        languagesEdited.current = true;
        setAdditionalLanguages((prev) =>
            prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
        );
    };

    return (
        <OnboardingShell
            title={STEP_TITLES[step]}
            tint={sitePrimaryColor}
            aside={<WhyCard {...STEP_ASIDES[step]} />}
        >
            {/* Progress track */}
            <div className="mb-8 flex gap-1.5">
                {STEPS.map((s, i) => (
                    <span key={s} className="h-1 flex-1 overflow-hidden rounded-full bg-border">
                        <span
                            className="block h-full rounded-full bg-accent-foreground transition-[width] duration-500"
                            style={{ width: i <= stepIndex ? '100%' : '0%' }}
                        />
                    </span>
                ))}
            </div>

            <div className="font-mono text-xs uppercase tracking-wide text-accent-foreground">
                {STEP_KICKERS[step]}
            </div>

            {step === 'website' && (
                <div className="mt-3 space-y-3">
                    <h1 className="text-3xl font-bold leading-tight tracking-tight">
                        What's your website?
                    </h1>
                    <div className="pt-2">
                        <div className="relative">
                            <Globe className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                autoFocus
                                placeholder="acme.com"
                                value={websiteUrl}
                                onChange={(e) => {
                                    if (extractDomain(e.target.value) !== domain) {
                                        languagesEdited.current = false;
                                        setDetectedLanguage(null);
                                        setLanguage('en');
                                        setAdditionalLanguages([]);
                                    }
                                    setWebsiteUrl(e.target.value);
                                }}
                                className="h-12 pl-10 text-base"
                            />
                        </div>
                        <div className="mt-2.5 flex items-start gap-2 rounded-lg bg-surface-sunken px-3 py-2.5 text-[13px] leading-snug text-muted-foreground">
                            <Info className="mt-0.5 size-4 flex-none" />
                            <span>
                                We'll read every page on this domain to train your agent.
                                {domain && (
                                    <>
                                        {' '}
                                        Setting up{' '}
                                        <strong className="text-foreground">{domain}</strong>.
                                    </>
                                )}
                            </span>
                        </div>
                        {canForce && (
                            <label className="mt-2.5 flex items-start gap-2 text-[13px] leading-snug text-muted-foreground">
                                <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    checked={forceOnboarding}
                                    onChange={(e) => setForceOnboarding(e.target.checked)}
                                />
                                <span>
                                    <strong className="text-foreground">
                                        Force re-onboard (staff)
                                    </strong>{' '}
                                    — reuse an existing test client for this domain instead of
                                    failing. Only affects <code>self_serve</code> clients; real
                                    clients are still protected.
                                </span>
                            </label>
                        )}
                    </div>
                </div>
            )}

            {step === 'company' && (
                <div className="mt-3 space-y-3">
                    <h1 className="text-3xl font-bold leading-tight tracking-tight">
                        What should we call you?
                    </h1>
                    <Input
                        autoFocus
                        placeholder="Acme"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="mt-2 h-12 text-base"
                    />
                </div>
            )}

            {step === 'language' && (
                <div className="mt-3 space-y-5">
                    <div className="space-y-2">
                        <h1 className="text-3xl font-bold leading-tight tracking-tight">
                            Your website languages
                        </h1>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                            Check the languages we detected and add any we missed. Rose always
                            replies in the visitor's language.
                        </p>
                    </div>

                    <div>
                        <div className="mb-2 text-[13px] font-semibold text-foreground">
                            Main website language
                        </div>
                        <Select
                            value={language}
                            onValueChange={(value) => {
                                languagesEdited.current = true;
                                setLanguage(value);
                                setAdditionalLanguages((current) =>
                                    current.filter((code) => code !== value)
                                );
                            }}
                        >
                            <SelectTrigger
                                className="data-[size=default]:h-12 w-full text-base"
                                aria-label="Main website language"
                            >
                                <span className="flex min-w-0 flex-1 items-center gap-2.5">
                                    <SelectValue />
                                    {detectedLanguage === language && domain && (
                                        <span className="truncate rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-accent-foreground">
                                            Detected from {domain}
                                        </span>
                                    )}
                                </span>
                            </SelectTrigger>
                            <SelectContent>
                                {LANGUAGES.map((lang) => (
                                    <SelectItem key={lang.value} value={lang.value}>
                                        {lang.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <div className="mb-2 text-[13px] font-semibold text-foreground">
                            Other website languages
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {visibleOtherLanguages.map((lang) => {
                                const on = additionalLanguages.includes(lang.value);
                                return (
                                    <button
                                        key={lang.value}
                                        type="button"
                                        onClick={() => toggleAdditionalLanguage(lang.value)}
                                        aria-pressed={on}
                                        className={cn(
                                            'inline-flex h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                                            on
                                                ? 'border-accent-foreground bg-accent/40 text-accent-foreground'
                                                : 'border-border text-foreground hover:border-accent-foreground/50'
                                        )}
                                    >
                                        {on && <Check className="size-3.5" aria-hidden="true" />}
                                        {lang.label}
                                    </button>
                                );
                            })}
                            {!showAllOtherLanguages &&
                                otherLanguages.length > visibleOtherLanguages.length && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAllOtherLanguages(true)}
                                        className="inline-flex h-11 items-center gap-2 rounded-full border border-dashed border-border px-4 text-sm font-medium text-muted-foreground transition-colors hover:border-accent-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                    >
                                        <Plus className="size-3.5" aria-hidden="true" /> More
                                    </button>
                                )}
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-8 flex items-center justify-between">
                {stepIndex > 0 ? (
                    <button
                        type="button"
                        onClick={goBack}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                    >
                        <ArrowLeft className="size-4" />
                        Back
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={handleSignOut}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <LogOut className="size-4" />
                        Sign out
                    </button>
                )}

                {isLastStep ? (
                    <Button type="button" size="lg" onClick={handleSubmit}>
                        Create my agent
                        <ArrowRight className="size-4" />
                    </Button>
                ) : (
                    <Button type="button" size="lg" onClick={goNext} disabled={!canContinue}>
                        Continue
                        <ArrowRight className="size-4" />
                    </Button>
                )}
            </div>
        </OnboardingShell>
    );
}
